import { act, screen } from '@testing-library/react';
import {
  describe,
  test,
  expect,
  vi,
  beforeAll,
  afterAll,
  beforeEach,
  afterEach,
} from 'vitest';
import '@testing-library/jest-dom/vitest';

import { AiResponse } from '../AiResponse.js';
import {
  render,
  setupMatchMediaMock,
  clearMatchMediaMock,
} from './vite-helpers.js';

const completeAnimation = async () => {
  await act(async () => {
    // 200ms animation duration + 50ms to finish rendering and animating up to 10 words
    vi.advanceTimersByTime(250);
  });
};

const sampleResponse = 'Some AI-generated response.';

describe('AiResponse', () => {
  beforeAll(() => {
    setupMatchMediaMock(false);
  });

  afterAll(() => {
    clearMatchMediaMock();
  });

  beforeEach(() => {
    vi.useFakeTimers();
  });

  afterEach(() => {
    vi.useRealTimers();
  });

  describe('onAnimationStateChange', () => {
    test('should fire when text is being streamed', async () => {
      // given
      const onAnimationStateChangeFn = vi.fn();

      // when
      render(
        <AiResponse onAnimationStateChange={onAnimationStateChangeFn}>
          {sampleResponse}
        </AiResponse>,
      );

      await completeAnimation();

      // then
      expect(onAnimationStateChangeFn).toHaveBeenCalledTimes(2);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(1, true);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(2, false);
    });

    test('should not fire when there is no text', async () => {
      // given
      const onAnimationStateChangeFn = vi.fn();
      const emptyResponse = '';

      // when
      render(
        <AiResponse onAnimationStateChange={onAnimationStateChangeFn}>
          {emptyResponse}
        </AiResponse>,
      );

      await completeAnimation();

      // then
      expect(onAnimationStateChangeFn).toHaveBeenCalledTimes(0);
    });

    test('should not fire when responseState is `static`', async () => {
      // given
      const onAnimationStateChangeFn = vi.fn();

      // when
      render(
        <AiResponse
          responseState="static"
          onAnimationStateChange={onAnimationStateChangeFn}
        >
          {sampleResponse}
        </AiResponse>,
      );

      await completeAnimation();

      // then
      expect(onAnimationStateChangeFn).toHaveBeenCalledTimes(0);
    });

    test('should fire when text is initially empty', async () => {
      // given
      const onAnimationStateChangeFn = vi.fn();
      const emptyResponse = '';

      // when
      const { rerender } = render(
        <AiResponse onAnimationStateChange={onAnimationStateChangeFn}>
          {emptyResponse}
        </AiResponse>,
      );

      // first check to ensure the second render will trigger the callback correctly
      // necessary because the second check might run before the first render's animation is done
      expect(onAnimationStateChangeFn).toHaveBeenCalledTimes(0);

      rerender(
        <AiResponse onAnimationStateChange={onAnimationStateChangeFn}>
          {sampleResponse}
        </AiResponse>,
      );
      await completeAnimation();

      // then
      expect(onAnimationStateChangeFn).toHaveBeenCalledTimes(2);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(1, true);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(2, false);
    });

    test('should not fire again when same text is provided', async () => {
      // given
      const onAnimationStateChangeFn = vi.fn();

      // when
      const { rerender } = render(
        <AiResponse onAnimationStateChange={onAnimationStateChangeFn}>
          {sampleResponse}
        </AiResponse>,
      );

      await completeAnimation();

      expect(onAnimationStateChangeFn).toHaveBeenCalledTimes(2);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(1, true);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(2, false);

      rerender(
        <AiResponse onAnimationStateChange={onAnimationStateChangeFn}>
          {sampleResponse}
        </AiResponse>,
      );

      await act(async () => {
        vi.advanceTimersByTime(1_000);
      });

      // then
      expect(onAnimationStateChangeFn).toHaveBeenCalledTimes(2);
    });

    test('should fire again when text is different', async () => {
      // given
      const onAnimationStateChangeFn = vi.fn();

      // when
      const { rerender } = render(
        <AiResponse onAnimationStateChange={onAnimationStateChangeFn}>
          {sampleResponse}
        </AiResponse>,
      );

      await completeAnimation();

      // first check to ensure the second render will trigger the callback correctly
      // necessary because the second check might run before the first render's animation is done
      expect(onAnimationStateChangeFn).toHaveBeenCalledTimes(2);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(1, true);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(2, false);

      rerender(
        <AiResponse onAnimationStateChange={onAnimationStateChangeFn}>
          Some other AI-generated response.
        </AiResponse>,
      );

      await completeAnimation();

      // then
      expect(onAnimationStateChangeFn).toHaveBeenCalledTimes(4);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(1, true);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(2, false);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(3, true);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(4, false);
    });

    test('should fire again when text is slightly different', async () => {
      // given
      const onAnimationStateChangeFn = vi.fn();

      // when
      const { rerender } = render(
        <AiResponse onAnimationStateChange={onAnimationStateChangeFn}>
          {sampleResponse}
        </AiResponse>,
      );

      await act(async () => {
        vi.advanceTimersByTime(1_000);
      });

      expect(onAnimationStateChangeFn).toHaveBeenCalledTimes(2);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(1, true);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(2, false);

      rerender(
        <AiResponse onAnimationStateChange={onAnimationStateChangeFn}>
          {`${sampleResponse} And some additional text.`}
        </AiResponse>,
      );

      await act(async () => {
        vi.advanceTimersByTime(1_000);
      });

      // then
      expect(onAnimationStateChangeFn).toHaveBeenCalledTimes(4);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(1, true);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(2, false);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(3, true);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(4, false);
    });

    test('should fire again when text is adapted during animation and responseState changes to `complete`', async () => {
      // given
      const onAnimationStateChangeFn = vi.fn();
      // this very long text ensures that the animation is not finished before we rerender with updated text
      const response =
        'Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet.';
      const updatedResponse = `
Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet.

Duis autem vel eum iriure dolor in hendrerit in vulputate velit esse molestie consequat, vel illum dolore eu feugiat nulla facilisis at vero eros et accumsan et iusto odio dignissim qui blandit praesent luptatum zzril delenit augue duis dolore te feugait nulla facilisi. Lorem ipsum dolor sit amet, consectetuer`;

      // when
      const { rerender } = render(
        <AiResponse
          onAnimationStateChange={onAnimationStateChangeFn}
          responseState="streaming"
        >
          {response}
        </AiResponse>,
      );

      // advance enough for some words to render but not all
      await act(async () => {
        vi.advanceTimersByTime(50);
      });

      // onAnimationStateChange should have been called once at the start of the animation
      expect(onAnimationStateChangeFn).toHaveBeenCalledTimes(1);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(1, true);

      rerender(
        <AiResponse
          onAnimationStateChange={onAnimationStateChangeFn}
          responseState="complete"
        >
          {updatedResponse}
        </AiResponse>,
      );

      // advance timers to complete the full animation + settling time
      await act(async () => {
        vi.advanceTimersByTime(10_000);
      });

      // there should be no additional calls, animation never stopped in between text changes
      // and should remain true until the full text is rendered.
      expect(onAnimationStateChangeFn).toHaveBeenCalledTimes(2);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(1, true);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(2, false);
    });

    test('should fire false when responseState switches to `static` mid-animation', async () => {
      // given
      const onAnimationStateChangeFn = vi.fn();
      const response =
        'Lorem ipsum dolor sit amet, consetetur sadipscing elitr.';

      // when
      const { rerender } = render(
        <AiResponse
          responseState="streaming"
          onAnimationStateChange={onAnimationStateChangeFn}
        >
          {response}
        </AiResponse>,
      );

      await act(async () => {
        vi.advanceTimersByTime(50);
      });

      expect(onAnimationStateChangeFn).toHaveBeenCalledTimes(1);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(1, true);

      rerender(
        <AiResponse
          responseState="static"
          onAnimationStateChange={onAnimationStateChangeFn}
        >
          {response}
        </AiResponse>,
      );

      // then — switching to static fires false synchronously (no timers needed)
      expect(onAnimationStateChangeFn).toHaveBeenCalledTimes(2);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(2, false);
    });

    test('should not fire again when responseState switches to `complete` after animation finished', async () => {
      // given
      const onAnimationStateChangeFn = vi.fn();

      // when
      const { rerender } = render(
        <AiResponse
          responseState="streaming"
          onAnimationStateChange={onAnimationStateChangeFn}
        >
          {sampleResponse}
        </AiResponse>,
      );

      await act(async () => {
        vi.advanceTimersByTime(10_000);
      });

      expect(onAnimationStateChangeFn).toHaveBeenCalledTimes(2);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(1, true);
      expect(onAnimationStateChangeFn).toHaveBeenNthCalledWith(2, false);

      rerender(
        <AiResponse
          responseState="complete"
          onAnimationStateChange={onAnimationStateChangeFn}
        >
          {sampleResponse}
        </AiResponse>,
      );

      await act(async () => {
        vi.advanceTimersByTime(1_000);
      });

      // then
      expect(onAnimationStateChangeFn).toHaveBeenCalledTimes(2);
    });
  });

  describe('responseState', () => {
    test('should render full text immediately when responseState is `static`', async () => {
      render(
        <AiResponse data-testid="ai-response" responseState="static">
          {sampleResponse}
        </AiResponse>,
      );

      // full text should be visible without waiting for animation
      expect(screen.getByText(sampleResponse)).toBeInTheDocument();
    });

    test('should animate and render the text word-by-word when responseState is `streaming`', async () => {
      // given
      vi.useFakeTimers();
      const response =
        'Some AI-generated response that would take over 50ms to render with the animated AiResponse component.';

      // when
      render(
        <AiResponse data-testid="ai-response" responseState="streaming">
          {response}
        </AiResponse>,
      );

      // then
      // initially no words are rendered yet
      expect(screen.queryByText(response)).toBeNull();

      // after advancing timers past the full animation duration, all text is rendered
      await act(async () => {
        // advance timers by 50ms to render only the first five words
        vi.advanceTimersByTime(25);
      });

      expect(screen.getByTestId('ai-response')).toHaveTextContent(
        /^Some AI-generated response that would$/,
      );
    });

    test('should animate and render the full text when responseState is `streaming`', async () => {
      // given
      vi.useFakeTimers();

      // when
      render(
        <AiResponse data-testid="ai-response" responseState="streaming">
          {sampleResponse}
        </AiResponse>,
      );

      // then
      // initially no words are rendered yet
      expect(screen.queryByText(sampleResponse)).toBeNull();

      // after advancing timers past the full animation duration, all text is rendered
      await completeAnimation();

      expect(screen.getByTestId('ai-response')).toHaveTextContent(
        sampleResponse,
      );
    });

    test('should animate and render the full text when responseState is `complete`', async () => {
      // given
      vi.useFakeTimers();

      // when
      render(
        <AiResponse data-testid="ai-response" responseState="complete">
          {sampleResponse}
        </AiResponse>,
      );

      // then
      // initially no words are rendered yet
      expect(screen.queryByText(sampleResponse)).toBeNull();

      // after advancing timers past the full animation duration, all text is rendered
      await completeAnimation();

      expect(screen.getByText(sampleResponse)).toBeInTheDocument();
    });
  });
});
